import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/* bl (owner 10/20): hover intent for the department row. An open panel must not close or change while the pointer is
   on its way into it — over the row's lower edge, through a gap, brushing a neighbouring department on a diagonal.
   - leaving the open department starts a grace instead of closing; coming back (the row or the panel) cancels it, and
     it holds while the pointer is still in the row moving down towards the panel
   - a neighbour replaces the open panel only once the pointer has been on it for the dwell and has come to rest on its
     word (the padding between two words is neutral): a pointer passing through, or slowing down on the way to the
     panel, is not a choice
   - the first panel opens at once; Escape, the disclosure button, blur and touch stay immediate
   - the same rules apply while the Explore panel (bl-explore, assets/bl-header.js) is open */
/** The pointer left the open department: wait this long before closing. */
const CLOSE_GRACE_MS = 300;
/** A neighbouring department takes over no sooner than this after the pointer reached it ... */
const SWITCH_DWELL_MS = 200;
/** ... and only once the pointer has stayed within SETTLE_PX for SETTLE_MS on its word (an overshoot that stops and
    turns back is still for ~150ms with its slow-down and slow start; a hand that wants the neighbour rests longer). */
const SETTLE_MS = 200;
const SETTLE_PX = 4;
/** Recent pointer motion that tells whether the pointer is still heading down into the open panel. */
const AIM_WINDOW_MS = 60;
/** While a switch or a close is waiting, look again after this. */
const SWITCH_RECHECK_MS = 50;

/* bl (R2, owner 10/21): touch on the department row. Touch-screen laptops used with a trackpad or a mouse now keep the
   row (assets/utilities.js isDrawerFirstDevice), and a finger has no hover: a tap on a department opens its panel
   instead of following its link (a second tap on the open department follows it, a tap on "More" toggles it), the
   finger leaving after the tap closes nothing, and a tap outside the menu closes the panel. Mouse and pen are as above. */
/** A click this soon after the touch that opened its department belongs to that tap. */
const TOUCH_TAP_MS = 1000;

/**
 * Hover events come from a mouse or a pen; touch keeps Horizon's immediate behaviour.
 * @param {Event} event
 * @param {string} type
 */
const isHover = (event, type) =>
  event.type === type && !(event instanceof PointerEvent && event.pointerType === 'touch');

/** @param {Event} event */
const isTouch = (event) => event instanceof PointerEvent && event.pointerType === 'touch';

/** The primary pointer: a change (a tablet's keyboard folded away) can switch the header between row and drawer. */
const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @typedef {Object} SubmenuMeasurement
 * @property {HTMLElement} item - The menu item that owns the active measurement.
 * @property {HTMLElement} target - The element whose height determines the open submenu height.
 * @property {boolean} isDefaultSlot - Whether the item is in the top-level menu.
 * @property {boolean} hasSubmenu - Whether the item has submenu content.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {SubmenuMeasurement | null} */
  #submenuMeasurement = null;

  /** @type {number | null} */
  #submenuResizeAnimationFrame = null;

  #submenuResizeObserver = new ResizeObserver((entries) => {
    const measurement = this.#submenuMeasurement;
    if (!measurement || this.#state.activeItem !== measurement.item) return;
    if (!entries.some((entry) => entry.target === measurement.target)) return;

    this.#scheduleSubmenuMeasurement(measurement);
  });

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #switchTimer;

  /** The neighbouring item waiting for its dwell before it replaces the open panel. @type {HTMLElement | null} */
  #pendingItem = null;

  /** Recent pointer positions inside the header while a panel is open or a switch is waiting. @type {Array<{ x: number, y: number, t: number }>} */
  #trail = [];

  /** @type {HTMLElement | null} */
  #trailHost = null;

  /** The department a touch has just opened, so the click that ends the same tap does not follow its link. @type {{ item: HTMLElement, t: number } | null} */
  #touchOpened = null;

  /** When a finger last pressed anywhere on the page (performance.now()). */
  #touchDownAt = -Infinity;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    // bl: the device rule (assets/utilities.js) once more, should the first-paint value (layout/theme.liquid, corrected
    // in sections/header.liquid) ever disagree with it; a no-op when they agree
    setHeaderMenuStyle();
    window.addEventListener('resize', this.#resizeListener);
    finePointerQuery.addEventListener('change', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('click', this.#onTouchClick, true);
    document.addEventListener('pointerdown', this.#onTouchOutside, { capture: true, passive: true });
    // bl: the overflow list measures the row as soon as it is on screen, often before the web fonts are in (the
    // fallback's wider words push one more department into "More", and the font swap does not always measure again):
    // ask it to measure once more when they have arrived (its own 'reflow' request; nothing moves when nothing changed)
    document.fonts?.ready.then(this.#remeasureRow);
    document.fonts?.addEventListener('loadingdone', this.#remeasureRow);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#trailHost = this.headerComponent;
    this.#trailHost?.addEventListener('pointermove', this.#recordPointer, { passive: true });
    if (this.#state.activeItem) {
      this.#startPointerTracking(this.#state.activeItem, null);
    }
    this.#resumeSubmenuResizeObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    finePointerQuery.removeEventListener('change', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('click', this.#onTouchClick, true);
    document.removeEventListener('pointerdown', this.#onTouchOutside, { capture: true });
    document.fonts?.removeEventListener('loadingdone', this.#remeasureRow);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#trailHost?.removeEventListener('pointermove', this.#recordPointer);
    this.#trailHost = null;
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupSubmenuResizeObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#clearCloseTimer();
    this.#clearPending();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /** The department row measured again (Horizon's overflow list, assets/overflow-list.js 'reflow'). */
  #remeasureRow = () => {
    if (!this.isConnected || this.#state.activeItem) return;
    this.refs.overflowMenu?.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
  };

  #overflowSubmenuListener = (/** @type {PointerEvent} */ event) => {
    // a finger leaves after every tap: it does not close the "More" panel (a tap outside does, #onTouchOutside)
    if (isTouch(event)) return;
    this.#deactivate();
  };

  /**
   * The click that ends the tap which opened a department's panel stays on the panel: no navigation.
   * @param {MouseEvent} event
   */
  #onTouchClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('.menu-list__link') : null;
    if (!link || !this.contains(link)) return;
    const item = findMenuItem(link);
    if (item && this.#consumeTouchOpen(item)) event.preventDefault();
  };

  /**
   * Whether `item` was opened by the touch this click ends (forgotten once asked).
   * @param {HTMLElement} item
   */
  #consumeTouchOpen(item) {
    const opened = this.#touchOpened;
    this.#touchOpened = null;
    return Boolean(opened && opened.item === item && performance.now() - opened.t < TOUCH_TAP_MS);
  }

  /**
   * Any finger press: noted (the blur it causes closes nothing); outside the menu it closes the open panel (a finger
   * never "leaves" it).
   * @param {PointerEvent} event
   */
  #onTouchOutside = (event) => {
    if (event.pointerType !== 'touch') return;
    this.#touchDownAt = performance.now();
    if (!this.#state.activeItem || event.composedPath().includes(this)) return;
    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * Close the open submenu on Escape and restore focus to its disclosure control.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;

    const control = this.#focusTargetAfterClose(item);
    this.#deactivate(item, { force: true });
    control.focus();
  };

  /**
   * Toggle a submenu from its dedicated disclosure button (click / Enter / Space).
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const item = findMenuItem(button);
    if (!item) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    // the click ending the tap that just opened it ("More" opens on the finger's pointerenter) keeps it open
    if (isExpanded && this.#consumeTouchOpen(item)) return;
    if (isExpanded) {
      // Close whatever is currently active. In the overflow panel the initially
      // selected item is tracked as `activeOverflowItem` while the More trigger is
      // the `activeItem`, so deactivating `item` directly would no-op; deactivate
      // the active item instead.
      this.#deactivate(this.#state.activeItem ?? item, { force: true });
      // Closing the overflow panel hides the focused disclosure, so return focus
      // to the visible More trigger; a top-level disclosure stays put.
      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== button) focusTarget.focus();
    } else {
      this.activate(event);
    }
  };

  /**
   * The control that should receive focus after a submenu closes. Overflow items
   * live in a panel that is hidden on close, so focus returns to the always-visible
   * More trigger instead of a now-inert control inside the panel.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }
    return this.#expandableFor(item);
  }

  /**
   * Return the element that carries the `aria-expanded` state for a menu item.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #expandableFor(item) {
    const listItem = item.closest('.menu-list__list-item');
    const disclosure = listItem?.querySelector('[ref="disclosure[]"]');
    return disclosure instanceof HTMLElement ? disclosure : item;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item: at once for the first panel, a click or a touch; after the hover intent
   * (#checkPending) when a neighbour takes over an open panel.
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    if (item == this.#state.activeItem) {
      // back on the open department or inside its panel: keep it
      this.#clearCloseTimer();
      this.#clearPending();
      return;
    }

    if (isHover(event, 'pointerenter') && (this.#state.activeItem || this.#otherPanelOpen())) {
      // a neighbour while a panel is open: the open one stays until the hover intent says otherwise
      if (item !== this.#pendingItem) this.#setPending(item);
      return;
    }

    // "More" tapped while its panel already shows one of its departments: that tap closes it (its click toggles)
    const moreShowing = item.closest('[slot="more"]') && this.#state.activeItem?.closest('[slot="overflow"]');

    this.#clearCloseTimer();
    this.#clearPending();
    this.#open(event, item);
    // a finger's tap opens the panel; the click that ends the same tap must not follow the department's link
    this.#touchOpened = event.type === 'pointerenter' && isTouch(event) && !moreShowing ? { item, t: performance.now() } : null;
  };

  /**
   * Close the open panel at once (bl: another header panel is opening, e.g. Explore).
   */
  close() {
    this.#clearPending();
    this.#deactivate(this.#state.activeItem, { force: true });
  }

  /** bl: the Explore panel (assets/bl-header.js) counts as an open panel for the hover intent. */
  #otherPanelOpen() {
    return Boolean(this.headerComponent?.querySelector('bl-explore[open]'));
  }

  /**
   * @param {HTMLElement} item
   */
  #setPending(item) {
    this.#clearPending();
    this.#pendingItem = item;
    this.#switchTimer = setTimeout(this.#checkPending, SWITCH_DWELL_MS);
  }

  #clearPending() {
    clearTimeout(this.#switchTimer);
    this.#switchTimer = undefined;
    this.#pendingItem = null;
  }

  #clearCloseTimer() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  /**
   * The dwell on a neighbour is over: switch once the pointer rests on its word, otherwise look again shortly (the
   * pointer leaving the neighbour drops the switch, see `deactivate`).
   */
  #checkPending = () => {
    this.#switchTimer = undefined;
    const item = this.#pendingItem;
    if (!item) return;

    const listItem = item.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement) || !listItem.matches(':hover')) {
      this.#clearPending();
      return;
    }

    // with nothing open any more (Explore closed meanwhile) this is a first open
    const panelOpen = Boolean(this.#state.activeItem) || this.#otherPanelOpen();
    const word = listItem.querySelector('.menu-list__link-title') ?? item;
    if (panelOpen && (!word.matches(':hover') || !this.#isSettled())) {
      this.#switchTimer = setTimeout(this.#checkPending, SWITCH_RECHECK_MS);
      return;
    }

    this.#clearPending();
    this.#clearCloseTimer();
    this.#open(null, item);
  };

  /**
   * Whether the pointer has stayed within SETTLE_PX over the last SETTLE_MS (a pointer that has not moved at all
   * is settled too).
   */
  #isSettled() {
    const since = performance.now() - SETTLE_MS;
    const trail = this.#trail;
    // the window starts at the position the pointer had at `since`: the last sample before it
    let start = 0;
    for (let index = trail.length - 1; index >= 0; index--) {
      if ((trail[index]?.t ?? 0) <= since) {
        start = index;
        break;
      }
    }
    const points = trail.slice(start);
    if (points.length < 2) return true;
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    return Math.max(...xs) - Math.min(...xs) <= SETTLE_PX && Math.max(...ys) - Math.min(...ys) <= SETTLE_PX;
  }

  /**
   * The pointer's movement over the last AIM_WINDOW_MS, or null when it is at rest.
   * @returns {{ dx: number, dy: number, y: number } | null}
   */
  #recentMotion() {
    const now = performance.now();
    const recent = this.#trail.filter((point) => now - point.t <= AIM_WINDOW_MS);
    const first = recent[0];
    const last = recent[recent.length - 1];
    if (!first || !last || first === last) return null;
    return { dx: last.x - first.x, dy: last.y - first.y, y: last.y };
  }

  /** @param {PointerEvent} event */
  #recordPointer = (event) => {
    if (!this.#state.activeItem && !this.#pendingItem) return;
    const now = performance.now();
    this.#trail.push({ x: event.clientX, y: event.clientY, t: now });
    while (this.#trail.length > 1 && now - (this.#trail[0]?.t ?? now) > 250) this.#trail.shift();
  };

  /**
   * The pointer left the open department: close after the grace unless it came back (to the row or the panel), a
   * neighbour is still deciding, or it is still in the row on its way down into the panel.
   * @param {number} [delay]
   */
  #scheduleClose(delay = CLOSE_GRACE_MS) {
    const item = this.#state.activeItem;
    if (!item) return;
    this.#clearCloseTimer();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      if (this.#state.activeItem !== item) return;
      if (item.closest('.menu-list__list-item')?.matches(':hover')) return;
      if (this.#pendingItem?.closest('.menu-list__list-item')?.matches(':hover')) {
        this.#scheduleClose();
        return;
      }
      const motion = this.#recentMotion();
      if (motion && motion.dy >= 2 && motion.y < this.getBoundingClientRect().bottom) {
        this.#scheduleClose(SWITCH_RECHECK_MS);
        return;
      }
      this.#deactivate(item);
    }, delay);
  }

  /**
   * Open an item's submenu now (Horizon's activate).
   * @param {PointerEvent | FocusEvent | MouseEvent | null} event
   * @param {HTMLElement} item
   */
  #open(event, item) {
    // Derive the slot context from the list item rather than `event.target`.
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    this.#cleanupSubmenuResizeObserver();

    if (previouslyActiveItem) {
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      // bl: focus inside the panel that is closing goes back to that department's disclosure; made inert, the panel
      // would drop it on <body> (and the blur would close the new panel too)
      if (previousSubmenu?.contains(document.activeElement)) {
        this.#expandableFor(previouslyActiveItem).focus({ preventScroll: true });
      }
      this.#expandableFor(previouslyActiveItem).ariaExpanded = 'false';
      if (previousSubmenu) previousSubmenu.inert = true;
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      this.#expandableFor(previouslyActiveOverflowItem).ariaExpanded = 'false';
      const previousOverflowSubmenu = findSubmenu(previouslyActiveOverflowItem);
      if (previousOverflowSubmenu) previousOverflowSubmenu.inert = true;
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    this.#expandableFor(item).ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      this.#expandableFor(overflowItem).ariaExpanded = 'true';
      // The More trigger auto-opens the first overflow item's submenu, so expose it.
      const activeOverflowSubmenu = findSubmenu(overflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = false;
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Expose the open submenu to focus and assistive tech. Collapsed submenus stay
      // inert so their contents cannot be reached (visibility:hidden alone can be
      // overridden by descendants that re-assert visibility).
      submenu.inert = false;

      const measurementTarget = isDefaultSlot ? submenu : this.overflowMenu;
      if (measurementTarget) {
        this.#startSubmenuResizeObserver(item, measurementTarget, isDefaultSlot, hasSubmenu);
      }
    } else {
      this.#setSubmenuHeights(0, 0);
    }

    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  }

  /**
   * Deactivate the active item: after the grace when the pointer leaves it, at once on blur and touch
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    // a finger leaves after every tap; the panel it opened stays (a tap outside closes it, #onTouchOutside)
    if (event.type === 'pointerleave' && isTouch(event)) return;

    const leavingItem = findMenuItem(event.target);
    if (isHover(event, 'pointerleave')) {
      // brushed a neighbour on the way: drop its switch, the open panel stays
      if (leavingItem && leavingItem === this.#pendingItem) {
        this.#clearPending();
        return;
      }
      if (leavingItem !== this.#state.activeItem) return;
    }
    // a blur from inside a panel that was just made inert (the browser dropping its focus) closes nothing
    if (event.type === 'blur') {
      const lost = event.composedPath?.()[0];
      if (lost instanceof Element && lost.closest('[inert]')) return;
      // a finger's press moves the focus to what it tapped: that blur closes nothing (the taps decide: another
      // department switches, "More" toggles, a tap outside closes)
      if (performance.now() - this.#touchDownAt < TOUCH_TAP_MS) return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    // Keep the submenu open while focus moves between the link, its disclosure
    // button, and the submenu contents — i.e. anywhere inside the same list item.
    const isMovingWithinItem =
      event.relatedTarget instanceof Node &&
      event.target instanceof Element &&
      event.target.contains(event.relatedTarget);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinItem || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    if (isHover(event, 'pointerleave')) {
      this.#scheduleClose();
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered,
    // unless the close was explicitly requested (Escape or the disclosure toggle),
    // which must close regardless of pointer position.
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#clearCloseTimer();
    this.#clearPending();
    this.#trail.length = 0;

    this.#cleanupSubmenuResizeObserver();
    this.#setSubmenuHeights(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    this.#expandableFor(item).ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#expandableFor(activeOverflowItem).ariaExpanded = 'false';
      const activeOverflowSubmenu = findSubmenu(activeOverflowItem);
      if (activeOverflowSubmenu) activeOverflowSubmenu.inert = true;
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Observe and measure the active submenu.
   * @param {HTMLElement} item
   * @param {HTMLElement} target
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @param {boolean} [deferMeasurement=false]
   */
  #startSubmenuResizeObserver(item, target, isDefaultSlot, hasSubmenu, deferMeasurement = false) {
    const measurement = { item, target, isDefaultSlot, hasSubmenu };
    this.#submenuMeasurement = measurement;
    this.#submenuResizeObserver.observe(target);
    if (deferMeasurement) {
      this.#scheduleSubmenuMeasurement(measurement);
    } else {
      this.#updateSubmenuHeight(measurement);
    }
  }

  /**
   * Restore resize tracking when an open menu is reconnected.
   */
  #resumeSubmenuResizeObserver() {
    const item = this.#state.activeItem;
    if (!item || !this.headerComponent) return;

    this.#cleanupSubmenuResizeObserver();

    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isDefaultSlot = slot === '';
    const isMoreTrigger = slot === 'more';
    const itemSubmenu = findSubmenu(item);
    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(this.#state.activeOverflowItem) : null;
    const hasSubmenu = Boolean(itemSubmenu || overflowItemSubmenu);
    const measurementTarget = isDefaultSlot ? itemSubmenu : this.overflowMenu;

    if (measurementTarget) {
      this.#startSubmenuResizeObserver(item, measurementTarget, isDefaultSlot, hasSubmenu, true);
    } else {
      this.#setSubmenuHeights(0, 0);
    }
  }

  /**
   * Schedule a height update for the active submenu.
   * @param {SubmenuMeasurement} measurement
   */
  #scheduleSubmenuMeasurement(measurement) {
    if (this.#submenuResizeAnimationFrame !== null) return;

    this.#submenuResizeAnimationFrame = requestAnimationFrame(() => {
      this.#submenuResizeAnimationFrame = null;
      if (this.#submenuMeasurement !== measurement || this.#state.activeItem !== measurement.item) return;

      this.#updateSubmenuHeight(measurement);
    });
  }

  /**
   * Measure and apply the active submenu height.
   * @param {SubmenuMeasurement} measurement
   */
  #updateSubmenuHeight(measurement) {
    if (this.#submenuMeasurement !== measurement || this.#state.activeItem !== measurement.item) return;

    let submenuHeight = measurement.target.offsetHeight;

    if (!measurement.isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      submenuHeight = measurement.hasSubmenu
        ? Math.max(measurement.target.offsetHeight, overflowListHeight)
        : overflowListHeight;
    }

    this.#setSubmenuHeights(submenuHeight, this.#getHeaderVisibleHeight());
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setSubmenuHeights(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;
    const submenuHeightValue = `${submenuHeight}px`;
    const fullOpenHeaderHeightValue = `${fullOpenHeaderHeight}px`;

    if (this.headerComponent.style.getPropertyValue('--submenu-height') !== submenuHeightValue) {
      this.headerComponent.style.setProperty('--submenu-height', submenuHeightValue);
    }
    if (this.headerComponent.style.getPropertyValue('--full-open-header-height') !== fullOpenHeaderHeightValue) {
      this.headerComponent.style.setProperty('--full-open-header-height', fullOpenHeaderHeightValue);
    }
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupSubmenuResizeObserver() {
    this.#submenuResizeObserver.disconnect();
    this.#submenuMeasurement = null;

    if (this.#submenuResizeAnimationFrame !== null) {
      cancelAnimationFrame(this.#submenuResizeAnimationFrame);
      this.#submenuResizeAnimationFrame = null;
    }
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // `element` may be the list item itself (hover/focus routed via the `<li>`) or a
  // descendant such as the disclosure button (click routed via the button).
  const listItem = element.closest('.menu-list__list-item') ?? element;
  return listItem.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
